import { useMemo, useState } from "react";

import clsx from "clsx";
import { Link } from "react-router-dom";

import { useSalesBreakdownConfigs } from "@components/data/sales-breakdown-configs";
import { GenericTooltip } from "@components/tooltip-container";
import { useUser } from "@components/user-context";
import { usePrettyLogEventPage } from "@components/v2/analytics";
import { VideoToolTip } from "@components/video-tooltip";
import {
  closestCenter,
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import { restrictToParentElement } from "@dnd-kit/modifiers";
import { arrayMove, SortableContext } from "@dnd-kit/sortable";
import { CreateBreakdownModal } from "@features/breakdown-v2/create-breakdown-modal";
import { CustomMetricsNewBadge } from "@features/custom-metrics/custom-metrics-modal";
import { useCustomMetricsContext } from "@features/custom-metrics/hooks/custom-metrics-provider";
import { type ChartsImperativeRef } from "@features/sales-v3/charts/charts";
import { useDoNotShowCharts } from "@features/sales-v3/table/contexts";
import { deriveComparisonDateRange } from "@features/sales-v3/utils/compare-dates-helpers";
import { formatDateRange } from "@features/sales-v3/utils/format-date-range";
import { EllipsisMenu, ShareButton } from "@features/shared";
import { useFeatureFlags } from "@hooks/http";
import {
  DEFAULT_DATE_RANGE_CHOICE_FOR_TIME_GRANULARITY,
  type ITimeGranularityV2,
} from "@north-beam/nb-common";
import { NewSalesExperienceSwitch } from "@pages/sales/new-sales-experience-switch";
import { AttributionWindowSelect } from "@pages/sales/report-body-control/attribution-window-select";
import { NbAccountingModeSelect } from "@pages/sales/report-body-control/nb-accounting-mode-select";
import { AttributionModelSelect } from "@shared/selects";
import { TimeGranularityV2Select } from "@shared/selects/time-granularity-v2-select";
import { Switch } from "@shared/switch";
import { generateAnonBase64Hash } from "@utils/anonymous";
import { usePrefs } from "@utils/client-side-preferences";

import {
  useAvailableBreakdownConfigs,
  usePageConfiguration,
  useSavedViews,
} from "../../hooks";
import { SavedViewsMenu } from "../../shared";

import { BreakdownButtonActive } from "./breakdown-button-active";
import { BreakdownButtonSelect } from "./breakdown-button-select";
import { CompareDateRangeMenu } from "./compare-date-range-menu";
import { DateFilters } from "./date-filters";
import { ExportMenu } from "./export-menu";
import { FilterButtonActive } from "./filter-button-active";
import { FilterButtonSelect } from "./filter-button-select";
import { SaveSavedViewsMenu } from "./save-saved-views-menu";

interface Props {
  chartsRef: React.MutableRefObject<ChartsImperativeRef | null>;
  onShowConfigurationDetailsModal(): void;
}

export const TopLevelFilters = ({
  chartsRef,
  onShowConfigurationDetailsModal,
}: Props) => {
  const { isDemoMode } = usePrefs();
  const prettyLogEventPage = usePrettyLogEventPage();
  const { user } = useUser();
  const { value: breakdownConfigs } = useSalesBreakdownConfigs();
  const { selectedSavedView } = useSavedViews();
  const { hasFeatureFlag } = useFeatureFlags();
  const { openModal: openCustomMetricsModal } = useCustomMetricsContext();
  const [isCreateBreakdownModalOpen, setIsCreateBreakdownModalOpen] =
    useState(false);

  const [doNotShowCharts, setDoNotShowCharts] = useDoNotShowCharts();
  const showCharts = useMemo(() => {
    return !doNotShowCharts;
  }, [doNotShowCharts]);
  const {
    attributionModel,
    attributionWindow,
    accountingMode,
    timeGranularity,
    dateRangeInterval,
    compareDateRange,
    breakdowns,
    filters,
    dateRange,
    setAttributionModel,
    setAttributionWindow,
    setAccountingMode,
    setTimeGranularity,
    setDateRange,
    setCompareDateRange,
    setBreakdowns,
    setFilters,
  } = usePageConfiguration();

  const pageContext = {
    dateRange,
    attributionModel,
    attributionWindow,
    accountingMode,
    timeGranularity,
    dateRangeInterval,
    compareDateRange,
    breakdowns,
    filters,
  };

  const sensors = useSensors(useSensor(PointerSensor));

  const availableBreakdownConfigs = useAvailableBreakdownConfigs(breakdowns);

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (active.id !== over?.id) {
      const oldIndex = breakdowns.findIndex(({ key }) => key === active.id);
      const newIndex = breakdowns.findIndex(({ key }) => key === over?.id);

      const newOrdering = arrayMove(breakdowns, oldIndex, newIndex);
      setBreakdowns(newOrdering);
    }
  };

  const isAttributionWindowVisible = accountingMode === "accrual";

  const compareDateRangeInterval = compareDateRange
    ? deriveComparisonDateRange(
        dateRangeInterval,
        compareDateRange,
        user.timezone,
        timeGranularity,
      )
    : null;
  const compareDateRangeDisplayText = compareDateRangeInterval
    ? formatDateRange(compareDateRangeInterval)
    : "";

  const isHourlyTimeGranularity = timeGranularity === "hourly";

  return (
    <>
      <div className="py-4 flex justify-between space-x-4">
        <div className="flex items-end space-x-3 grow">
          <div className="text-2xl font-bold max-w-xxs truncate">
            {generateAnonBase64Hash({
              value: selectedSavedView?.name,
              prefix: "quick-filter",
              doAnon: isDemoMode && !selectedSavedView?.is_template,
            })}
          </div>
          <SavedViewsMenu placement="bottom-start" />
          <div className="border-l border-gray-1 h-8" />
          <AttributionModelSelect
            maxWidth="160px"
            attributionModel={attributionModel}
            onChange={(value) => {
              const newAttributionModel = value;
              prettyLogEventPage({
                domain: "Top Level Filters",
                action: "onChange",
                additionalActionInfo: "Set Attribution Model",
                data: {
                  newAttributionModel,
                  pageContext,
                },
              });

              setAttributionModel(newAttributionModel);
            }}
          />
          {isAttributionWindowVisible && (
            <GenericTooltip
              className={isHourlyTimeGranularity ? "cursor-not-allowed" : ""}
              content={
                isHourlyTimeGranularity ? (
                  <>
                    Attribution window selection unavailable
                    <br />
                    for the time granularity selected
                  </>
                ) : null
              }
              placement="bottom"
              noInfoCircle
            >
              <AttributionWindowSelect
                maxWidth="160px"
                attributionModel={attributionModel}
                attributionWindow={attributionWindow}
                isDisabled={isHourlyTimeGranularity}
                timeGranularity="daily"
                onChange={(value) => {
                  const newAttributionWindow = value;
                  prettyLogEventPage({
                    domain: "Top Level Filters",
                    action: "onChange",
                    additionalActionInfo: "Set Attribution Window",
                    data: {
                      newAttributionWindow,
                      pageContext,
                    },
                  });

                  setAttributionWindow(newAttributionWindow);
                }}
              />
            </GenericTooltip>
          )}
          <NbAccountingModeSelect
            maxWidth="200px"
            nbAccountingMode={accountingMode}
            isLoading={false}
            icon={null}
            onChange={(value) => {
              const newAccountingMode = value;
              prettyLogEventPage({
                domain: "Top Level Filters",
                action: "onChange",
                additionalActionInfo: "Set Accounting Mode",
                data: {
                  newAccountingMode,
                  pageContext,
                },
              });

              setAccountingMode(newAccountingMode);
            }}
          />
        </div>
        <div className="flex space-x-2 items-end">
          <SaveSavedViewsMenu />
          <ShareButton />
          <ExportMenu chartsRef={chartsRef} />
          <EllipsisMenu>
            <EllipsisMenu.Item
              as="button"
              onClick={onShowConfigurationDetailsModal}
            >
              Configuration details
            </EllipsisMenu.Item>
            {hasFeatureFlag("customMetricsV1") && (
              <EllipsisMenu.Item
                as="button"
                onClick={() => openCustomMetricsModal()}
              >
                Custom metrics
                <CustomMetricsNewBadge className="ml-2" />
              </EllipsisMenu.Item>
            )}
            <EllipsisMenu.Item
              as="button"
              onClick={() => {
                // onClick
              }}
            >
              <div className="flex items-center w-full">
                <div className="mr-2">Show charts experience</div>
                <Switch
                  size="small"
                  value={showCharts}
                  onChange={() => {
                    prettyLogEventPage({
                      domain: "Top Level Filters",
                      action: "onChange",
                      additionalActionInfo: "Toggle Chart Experience",
                    });

                    setDoNotShowCharts((prev) => !prev);
                  }}
                  labelClassname="ml-auto"
                  id="show-charts-experience-switch"
                />
              </div>
            </EllipsisMenu.Item>
            <EllipsisMenu.Item
              as="button"
              onClick={() => {
                // Do nothing
              }}
            >
              <NewSalesExperienceSwitch switchClassname="ml-auto" basic />
            </EllipsisMenu.Item>
          </EllipsisMenu>
        </div>
      </div>
      <div
        className={clsx(
          "-mx-6 px-6 py-4 bg-cosmic-5 border-y border-gray",
          "text-xs flex justify-between",
        )}
      >
        <div className="flex flex-grow">
          <div className="flex items-center mr-2 font-bold min-w-max">
            <div>FILTER BY</div>
            <VideoToolTip
              videoUrl="https://www.youtube.com/embed/6weF10eVM7E?si=qBEiODOHMPVXjm9P"
              contentDetails={{
                title: "Filtering",
                content:
                  "Filters impact both chart and table views, and help you refine your " +
                  "data to make it more meaningful and accurate for analysis.",
                kbLink: "https://docs.northbeam.io/docs/sales-page",
              }}
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {filters.map((e) => (
              <FilterButtonActive
                key={e.key}
                breakdown={e}
                onDelete={() => {
                  const newFilters = filters.filter((v) => v.key !== e.key);

                  prettyLogEventPage({
                    domain: "Top Level Filters",
                    action: "onClick",
                    additionalActionInfo: "Delete Page Filters",
                    data: {
                      newFilters,
                      pageContext,
                    },
                  });

                  setFilters(newFilters);
                }}
              />
            ))}
            <FilterButtonSelect
              breakdownConfigs={breakdownConfigs}
              breakdowns={filters}
              onChange={(value) => {
                prettyLogEventPage({
                  domain: "Top Level Filters",
                  action: "onChange",
                  additionalActionInfo: "Change Page Filters",
                  data: {
                    newFilters: value,
                    pageContext,
                  },
                });

                setFilters(value);
              }}
            />
          </div>
        </div>
        <div className="flex items-center">
          <div className="flex items-center mr-2 font-bold min-w-max">
            <div>BREAKDOWN BY</div>
            <VideoToolTip
              videoUrl="https://www.youtube.com/embed/8jQksVYzBFE?si=5HwVedmaymDFtQ5S"
              contentDetails={{
                title: "Breakdowns",
                content:
                  "Breakdowns allow you to segment and analyze data by creating nested tables " +
                  "and chart views based on marketing tactics.",
                kbLink:
                  "https://docs.northbeam.io/docs/saved-views-and-breakdowns",
              }}
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              modifiers={[restrictToParentElement]}
              onDragEnd={handleDragEnd}
              autoScroll={false}
            >
              <SortableContext items={breakdowns.map(({ key }) => key)}>
                {breakdowns.map((e, idx) => (
                  <BreakdownButtonActive
                    key={e.key}
                    breakdownConfigs={[
                      ...availableBreakdownConfigs,
                      breakdownConfigs.find((v) => v.key === e.key)!,
                    ].filter((e) => e)}
                    breakdown={e}
                    position={idx + 1}
                    showPosition={breakdowns.length > 1}
                    onChange={(newValue) => {
                      const newBreakdowns = breakdowns.map((v) =>
                        v.key === e.key ? newValue : v,
                      );

                      prettyLogEventPage({
                        domain: "Top Level Filters",
                        action: "onChange",
                        additionalActionInfo: "Change Page Breakdowns",
                        data: {
                          newBreakdowns,
                          pageContext,
                        },
                      });

                      setBreakdowns(newBreakdowns);
                    }}
                    onDelete={() => {
                      const newBreakdowns = breakdowns.filter(
                        (v) => v.key !== e.key,
                      );

                      prettyLogEventPage({
                        domain: "Top Level Filters",
                        action: "onChange",
                        additionalActionInfo: "Delete Page Breakdowns",
                        data: {
                          newBreakdowns,
                          pageContext,
                        },
                      });

                      setBreakdowns(newBreakdowns);
                    }}
                  />
                ))}
              </SortableContext>
            </DndContext>
            {availableBreakdownConfigs.length > 0 && (
              <BreakdownButtonSelect
                breakdownConfigs={availableBreakdownConfigs}
                placeholder={breakdowns.length > 0 ? "Add..." : undefined}
                onChange={(value) => {
                  const newBreakdowns = [...breakdowns, value];

                  prettyLogEventPage({
                    domain: "Top Level Filters",
                    action: "onChange",
                    additionalActionInfo: "Add Page Breakdowns",
                    data: {
                      newBreakdowns,
                      pageContext,
                    },
                  });

                  setBreakdowns(newBreakdowns);
                }}
              />
            )}
            <EllipsisMenu>
              <>
                <EllipsisMenu.Item
                  as="button"
                  onClick={() => setIsCreateBreakdownModalOpen(true)}
                >
                  Create a new breakdown
                  {hasFeatureFlag("showBreakdownV2NewBadge") && (
                    <CustomMetricsNewBadge className="ml-2" />
                  )}
                </EllipsisMenu.Item>
                <EllipsisMenu.Item
                  as={Link}
                  to={user.pathFromRoot("/breakdowns")}
                >
                  Manage breakdowns
                  {hasFeatureFlag("showBreakdownV2NewBadge") && (
                    <CustomMetricsNewBadge className="ml-2" />
                  )}
                </EllipsisMenu.Item>
              </>
            </EllipsisMenu>
          </div>
        </div>
      </div>
      <div className={clsx("pt-4 flex gap-3", { "pb-4": !doNotShowCharts })}>
        <TimeGranularityV2Select
          label="GRANULARITY"
          timeGranularity={timeGranularity}
          onChange={(value: ITimeGranularityV2) => {
            prettyLogEventPage({
              domain: "Top Level Filters",
              action: "onChange",
              additionalActionInfo: "Set Time Granularity",
              data: {
                newTimeGranularity: value,
                pageContext,
              },
            });

            if (value === "hourly") {
              setDateRange(
                DEFAULT_DATE_RANGE_CHOICE_FOR_TIME_GRANULARITY[value],
              );
            } else {
              setDateRange({
                startDate: dateRangeInterval.startDate,
                endDate: dateRangeInterval.endDate,
                type: "fixed",
              });
            }

            setTimeGranularity(value);
          }}
          width="92px"
          icon={null}
        />
        <div className="text-xs">
          <div
            className={clsx(
              "text-xs text-interstellar+2 mb-1 leading-4 tracking-[0.025em]",
              "mt-[3px] cursor-default",
            )}
          >
            TIME PERIOD
            <span className="ml-2 text-gray+1">
              {formatDateRange(dateRangeInterval)}
            </span>
          </div>
          <DateFilters />
        </div>
        <div>
          <div
            className={clsx(
              "text-xs text-interstellar+2 mb-1 leading-4 tracking-[0.025em]",
              "mt-[3px] cursor-default flex items-center",
            )}
          >
            TIME COMPARISON
            <span className="ml-2 text-gray+1">
              {compareDateRangeDisplayText}
            </span>
            <VideoToolTip
              videoUrl="https://www.youtube.com/embed/D3kl9ji68dk?si=yNsRv2vyO9ZYU_Mu"
              contentDetails={{
                title: "Time comparisons",
                content:
                  "A guided walk through how to compare time periods like YoY, WoW, " +
                  "and custom time periods.",
                kbLink:
                  "https://info.northbeam.io/knowledge/how-do-i-compare-time-periods-on-the-sales-page",
              }}
            />
          </div>
          <CompareDateRangeMenu
            className="h-8 w-64"
            timeGranularity={timeGranularity}
            compareDateRange={compareDateRange}
            onCompareDateRangeUpdate={(value) => {
              prettyLogEventPage({
                domain: "Top Level Filters",
                action: "onChange",
                additionalActionInfo: "Set Compare Date Range",
                data: {
                  newCompareDateRange: value,
                  pageContext,
                },
              });

              setCompareDateRange(value);
            }}
          />
        </div>
      </div>
      {isCreateBreakdownModalOpen && (
        <CreateBreakdownModal
          onClose={() => setIsCreateBreakdownModalOpen(false)}
          isOpen={isCreateBreakdownModalOpen}
        />
      )}
    </>
  );
};
